import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ConsentDataRow from "@/components/consent/ConsentDataRow";
import ConsentStatusBadge from "@/components/consent/ConsentStatusBadge";
import ConsentActionDialog, { ConsentDialogVariant } from "@/components/consent/ConsentActionDialog";

import { resolveClientAsset } from "@/utils/clientBranding";

import { ConsentAnswerRequest } from "@/models/shared/consents/ConsentAuthorizationRequest";
import {
  AppConsents,
  resolveAppDecisionDate,
  resolveAppState,
} from "@/models/site-credentials/consents/UserAppConsents";

type ConsentAppCardProps = {
  app: AppConsents;
  loading?: boolean;
  onSubmit: (app: AppConsents, answers: Array<ConsentAnswerRequest>) => void;
};

type ConsentActionKey =
  | "revoke_all"
  | "save_changes"
  | "authorize_selected"
  | "authorize_mandatory_only";

/** As duas variantes de autorizacao partilham o mesmo popup de confirmacao. */
const DIALOG_BY_ACTION: Record<ConsentActionKey, ConsentDialogVariant> = {
  revoke_all: "revoke_all",
  save_changes: "save_changes",
  authorize_selected: "authorize",
  authorize_mandatory_only: "authorize",
};

const buildSelection = (app: AppConsents): Record<string, boolean> =>
  Object.fromEntries(
    app.items.filter((item) => !item.isMandatory).map((item) => [item.id, item.state === "Accepted"])
  );

export default function ConsentAppCard({ app, loading, onSubmit }: ConsentAppCardProps) {
  const { t } = useTranslation();

  const [selected, setSelected] = useState<Record<string, boolean>>(() => buildSelection(app));
  const [dialogOpen, setDialogOpen] = useState(false);

  const asset = useMemo(() => resolveClientAsset(app.assetAlias), [app.assetAlias]);
  const appState = resolveAppState(app);
  const decisionDate = resolveAppDecisionDate(app);

  const optionalItems = app.items.filter((item) => !item.isMandatory);
  const isDirty = optionalItems.some((item) => selected[item.id] !== (item.state === "Accepted"));
  const anyOptionalOn = optionalItems.some((item) => selected[item.id]);

  const isAllowed = appState === "Accepted";
  const isRevokeAll = isAllowed && !isDirty;

  const actionKey: ConsentActionKey = isAllowed
    ? isDirty
      ? "save_changes"
      : "revoke_all"
    : anyOptionalOn
      ? "authorize_selected"
      : "authorize_mandatory_only";

  const statusLine = decisionDate
    ? t(isAllowed ? "site_credentials.consents.granted_on" : "site_credentials.consents.refused_on", {
      date: format(new Date(decisionDate), "dd-MM-yyyy"),
    })
    : t(isAllowed ? "site_credentials.consents.status.allowed" : "site_credentials.consents.status.refused");

  const toggle = (id: string) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const buildAnswers = (revokeEverything: boolean): Array<ConsentAnswerRequest> =>
    app.items.map((item) => ({
      id: item.id,
      answer: revokeEverything ? false : item.isMandatory || !!selected[item.id],
    }));

  /** Nada e gravado sem passar pelo popup: a acao so abre a confirmacao. */
  const handleAction = () => setDialogOpen(true);

  const handleConfirm = () => {
    setDialogOpen(false);
    onSubmit(app, buildAnswers(isRevokeAll));
  };

  return (
    <AccordionItem value={app.clientId} variant="container" className="my-0 last:border py-4">
      <AccordionTrigger className="py-0">
        <div className="flex min-w-0 flex-1 items-center gap-8">
          <img
            src={asset.icon}
            alt={asset.altText ?? app.clientName}
            className="size-16 shrink-0 object-contain"
          />
          <div className="flex min-w-0 flex-col text-left">
            <span className="truncate font-bold text-[var(--on-surface-default)]">{app.clientName}</span>
            <span className="truncate text-xs font-normal text-[var(--on-surface-variant)]">{statusLine}</span>
          </div>
        </div>
        <ConsentStatusBadge state={appState} className="self-center" />
      </AccordionTrigger>

      <AccordionContent className="flex flex-col gap-8 pb-8">
        {!isAllowed && (
          <span className="text-sm text-[var(--on-surface-variant)]">
            {t("site_credentials.consents.shared_data_hint")}
          </span>
        )}

        {app.items.map((item) => (
          <ConsentDataRow
            key={item.id}
            item={item}
            checked={item.isMandatory || !!selected[item.id]}
            disabled={loading}
            onToggle={toggle}
          />
        ))}

        <Button
          type="button"
          variant="secondary"
          disabled={loading}
          onClick={handleAction}
          aria-label={t(`site_credentials.consents.actions.${actionKey}_aria`, { clientName: app.clientName })}
          className="mt-4 w-full min-w-0 rounded-full"
        >
          {t(`site_credentials.consents.actions.${actionKey}`)}
        </Button>
      </AccordionContent>

      <ConsentActionDialog
        open={dialogOpen}
        variant={DIALOG_BY_ACTION[actionKey]}
        clientName={app.clientName}
        loading={loading}
        onOpenChange={setDialogOpen}
        onConfirm={handleConfirm}
      />
    </AccordionItem>
  );
}
